import { LIST_SESSIONS_NAME } from "@/lib/ai/tools/list-sessions/constants";
import type { ListSessionsOutput } from "@/lib/ai/tools/list-sessions/server";
import { defineDatasetSpec } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListNumber,
  toListText,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const SESSIONS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_SESSIONS_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.sessions" }),
  fields: [
    { key: "gaSessionId", role: "metric", type: "number", unit: "count" },
    { key: "gaSessionNumber", role: "metric", type: "number", unit: "count" },
    { key: "userPseudoId", role: "dim", type: "string" },
    { key: "firstPageLocation", role: "dim", type: "string" },
    { key: "firstPageLocationURLGroupId", role: "dim", type: "string" },
    { key: "firstPageLocationURLGroupName", role: "dim", type: "string" },
    { key: "firstPageTitle", role: "dim", type: "string" },
    { key: "secondPageLocation", role: "dim", type: "string" },
    { key: "secondPageLocationURLGroupId", role: "dim", type: "string" },
    { key: "secondPageLocationURLGroupName", role: "dim", type: "string" },
    { key: "secondPageTitle", role: "dim", type: "string" },
    { key: "pageReferrer", role: "dim", type: "string" },
    { key: "pageLocationCount", role: "metric", type: "number", unit: "count" },
    { key: "pageViewCount", role: "metric", type: "number", unit: "count" },
    {
      key: "sessionDuration",
      role: "metric",
      type: "number",
      unit: "duration",
    },
    { key: "sessionStartTime", role: "dim", type: "string" },
    { key: "sessionEndTime", role: "dim", type: "string" },
    { key: "source", role: "dim", type: "string" },
    { key: "medium", role: "dim", type: "string" },
    { key: "campaign", role: "dim", type: "string" },
    { key: "term", role: "dim", type: "string" },
    { key: "content", role: "dim", type: "string" },
    { key: "geography_country", role: "dim", type: "string" },
    { key: "geography_region", role: "dim", type: "string" },
    { key: "geography_city", role: "dim", type: "string" },
    { key: "device_category", role: "dim", type: "string" },
    { key: "device_os", role: "dim", type: "string" },
    { key: "device_browser", role: "dim", type: "string" },
    { key: "sessionExplorerPath", role: "dim", type: "string" },
    { key: "sessionExplorerUrl", role: "dim", type: "string" },
  ] as const,
  defaults: { category: "userPseudoId", value: "pageViewCount" },
  notes: "Table dataset normalized from list_sessions output.",
});

function toSessionRows(output: Partial<ListSessionsOutput>): VizDatasetRow[] {
  const sessions = Array.isArray(output.sessions) ? output.sessions : [];

  return sessions.map((session) => ({
    gaSessionId: toListNumber(session.gaSessionId),
    gaSessionNumber: toListNumber(session.gaSessionNumber),
    userPseudoId: toListText(session.userPseudoId),
    firstPageLocation: toListText(session.firstPageLocation),
    firstPageLocationURLGroupId: toListText(
      session.firstPageLocationURLGroup?.id,
    ),
    firstPageLocationURLGroupName: toListText(
      session.firstPageLocationURLGroup?.name,
    ),
    firstPageTitle: toListText(session.firstPageTitle),
    secondPageLocation: toListText(session.secondPageLocation),
    secondPageLocationURLGroupId: toListText(
      session.secondPageLocationURLGroup?.id,
    ),
    secondPageLocationURLGroupName: toListText(
      session.secondPageLocationURLGroup?.name,
    ),
    secondPageTitle: toListText(session.secondPageTitle),
    pageReferrer: toListText(session.pageReferrer),
    pageLocationCount: toListNumber(session.pageLocationCount),
    pageViewCount: toListNumber(session.pageViewCount),
    sessionDuration: toListNumber(session.sessionDuration),
    sessionStartTime: toListText(session.sessionStartTime),
    sessionEndTime: toListText(session.sessionEndTime),
    source: toListText(session.source),
    medium: toListText(session.medium),
    campaign: toListText(session.campaign),
    term: toListText(session.term),
    content: toListText(session.content),
    geography_country: toListText(session.geography.country),
    geography_region: toListText(session.geography.region),
    geography_city: toListText(session.geography.city),
    device_category: toListText(session.device.category),
    device_os: toListText(session.device.os),
    device_browser: toListText(session.device.browser),
    sessionExplorerPath: toListText(session.sessionExplorerPath),
    sessionExplorerUrl: toListText(session.sessionExplorerUrl),
  }));
}

/**
 * list_sessions 出力を table-only dataset へ正規化する。
 */
export function buildListSessionsDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput =
    typeof output === "object" && output !== null
      ? (output as Partial<ListSessionsOutput>)
      : {};
  const rows = toSessionRows(typedOutput);
  const dataset = buildListTableDataset({
    id: SESSIONS_DATASET_SPEC.id,
    datasetFamily: SESSIONS_DATASET_SPEC.profile.datasetFamily,
    variant: SESSIONS_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: SESSIONS_DATASET_SPEC.fields,
    defaults: SESSIONS_DATASET_SPEC.defaults,
    notes: SESSIONS_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
